import { isBackofficePlayground } from '../../../config/deployment';
import { getThankYouPagePatterns } from '../../../config/SiteConfigManager';
import { logger } from '../../../utils/core/logger';
import { matchesUrlPattern } from '../../../utils/web/urlUtils';
import type { FormDetectionContext, FormDetectionStrategy, FormElement } from '../types';

// Strategy 4: Thank-You Page Detection (URL-based form submission confirmation)
// This strategy detects when users land on thank-you/confirmation pages after form submission
export class ThankYouPageStrategy implements FormDetectionStrategy {
    name = 'thankyou_page_detected';
    // Fire once per configured thank-you pattern per session. Deduping by matched
    // pattern (not by exact href) collapses the many URLs that resolve to the same
    // thank-you page — SPA step navigation and tracking-param churn (_gl, _gcl_aw,
    // token, rose_session_id…) — which would otherwise re-fire the same
    // conversion. Distinct patterns still fire independently (IX-5113).
    private firedPatterns = new Set<string>();
    private originalPushState: typeof history.pushState | null = null;
    private originalReplaceState: typeof history.replaceState | null = null;
    private popstateListener: (() => void) | null = null;

    // This strategy doesn't track forms - it tracks page URLs
    canHandle(_form: FormElement, _context: FormDetectionContext): boolean {
        return false;
    }

    // No-op for forms - this strategy monitors URLs instead
    apply(_form: FormElement, _context: FormDetectionContext): void {
        // No-op
    }

    /**
     * Check if current URL matches any thank-you page patterns
     * Called on page load and SPA navigation
     */
    checkCurrentUrl(context: FormDetectionContext): void {
        const currentUrl = window.location.href;

        const patterns = getThankYouPagePatterns(context.domain);
        if (patterns.length === 0) {
            return;
        }

        const matched = patterns.find((pattern) => matchesUrlPattern(currentUrl, pattern));
        // Skip if no pattern matches, or this pattern already fired this session.
        if (!matched || this.firedPatterns.has(matched)) {
            return;
        }
        this.firedPatterns.add(matched);

        logger.debug('✅ Thank-you page detected, tracking form submission', {
            url: currentUrl,
            pattern: matched,
        });

        context.onFormSubmit({
            detectionMethod: this.name,
            pageUrl: currentUrl,
            status: 'success',
            indicators: [`thankyou_page_match:${matched}`],
        });
    }

    /**
     * Setup URL change listeners for SPA navigation
     * Monitors pushState, replaceState, and popstate events
     */
    setupUrlMonitoring(context: FormDetectionContext): void {
        // The backoffice playground embeds the widget inside its own SPA, so `window.history`
        // here is the backoffice's react-router history. Patching it corrupts host navigation:
        // this wrapper calls `originalPushState?.(...)`, so a copy stranded by another patcher's
        // restore race becomes a silent no-op that freezes sidebar nav until reload. No real
        // thank-you-page navigation happens in the simulated preview, so skip monitoring.
        if (isBackofficePlayground()) {
            return;
        }

        // Listen for popstate (back/forward navigation)
        this.popstateListener = () => this.checkCurrentUrl(context);
        window.addEventListener('popstate', this.popstateListener);

        // Wrap pushState for SPA navigation
        this.originalPushState = history.pushState.bind(history);
        history.pushState = (...args: Parameters<typeof history.pushState>) => {
            this.originalPushState?.(...args);
            // Use setTimeout to ensure URL is updated before checking
            setTimeout(() => this.checkCurrentUrl(context), 0);
        };

        // Wrap replaceState for SPA navigation
        this.originalReplaceState = history.replaceState.bind(history);
        history.replaceState = (...args: Parameters<typeof history.replaceState>) => {
            this.originalReplaceState?.(...args);
            setTimeout(() => this.checkCurrentUrl(context), 0);
        };

        logger.debug('🔍 Thank-you page URL monitoring started');
    }

    /**
     * Clean up URL monitoring listeners and restore original history methods
     */
    cleanup(): void {
        // Remove popstate listener
        if (this.popstateListener) {
            window.removeEventListener('popstate', this.popstateListener);
            this.popstateListener = null;
        }

        // Restore original history methods
        if (this.originalPushState) {
            history.pushState = this.originalPushState;
            this.originalPushState = null;
        }
        if (this.originalReplaceState) {
            history.replaceState = this.originalReplaceState;
            this.originalReplaceState = null;
        }

        logger.debug('🛑 Thank-you page URL monitoring stopped');
    }
}
